import * as React from 'react';
import { Tooltip, TooltipPosition } from '@cian/tooltip-component';
import { Label, DropdownField } from '@cian/components';
import { TextField } from '@cian/text-field-component';
import { EmailTooltip } from '../email_tooltip';
import { License } from '../../license';
import { Button } from '@cian/button-component';
import { Checkbox } from '@cian/checkbox-component';
import { SignedPopup } from '../signed_popup';
import { mergeStyles } from '@cian/utils';
import {
  getFormattedLabel,
  TNotificationFrequencyValue,
  NOTIFICATION_FREQUENCY,
  mapNotificationFrequency,
} from '../utils/notification_frequency';
import { getJsonQuery } from '../utils/get_json_query';
import { IOfferSchema, IUserSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { getEmailError } from '../utils/get_email_error';
import * as styles from './subscription_form.css';
import { trackSubscribeSave, trackSubscribeWithNews } from './tracking';
import { getTitle } from './utils';
import {
  ESavedSearchType,
  ICreateSubscriptionRequest,
} from '../../../repositories/monolithPython/v1/create-subscription';

export interface ISubscriptionFormProps {
  isSubscribe: boolean;
  offer: IOfferSchema;
  user: IUserSchema | null;
  globalError?: string;
  isLoad: boolean;
  pushSubscribed: boolean;
  fetchCreateSubscription(parameters: ICreateSubscriptionRequest): Promise<void>;
}

export interface ISubscriptionFormState {
  title: string;
  titleInputInvalid: boolean;
  email: string;
  emailInputInvalid: boolean;
  emailError: string;
  isNews: boolean;
  isPushCheck: boolean;
  notificationFrequency: TNotificationFrequencyValue;
  isEmailTooltipOpen: boolean;
}

export class SubscriptionForm extends React.Component<ISubscriptionFormProps, ISubscriptionFormState> {
  public constructor(props: ISubscriptionFormProps) {
    super(props);

    this.state = {
      title: getTitle(props.offer),
      titleInputInvalid: false,
      email: props.user && props.user.email || '',
      emailInputInvalid: false,
      emailError: '',
      isNews: true,
      isPushCheck: true,
      notificationFrequency: 1,
      isEmailTooltipOpen: false,
    };
  }

  public render() {
    const {
      title, email, isNews, isPushCheck, isEmailTooltipOpen,
      titleInputInvalid, emailInputInvalid, emailError,
    } = this.state;
    const { globalError, isLoad, pushSubscribed, user, isSubscribe } = this.props;

    if (isSubscribe) {
      return (
        <SignedPopup
          email={email}
          userEmail={user && user.email}
          isAuthenticated={user && user.isAuthenticated}
        />
      );
    }

    return (
      <>
        <form className={styles['form']} action="#" onSubmit={this.handleFormSubmit}>
          <Tooltip tooltipContent={globalError || ''}  position={TooltipPosition.BOTTOM_LEFT}>
            <Label
              containerStyle={styles['field']}
              labelWrapperStyle={styles['label-container']}
              labelStyle={styles['label']}
              label="Название поиска"
            >
              <TextField
                value={title}
                disabled={isLoad}
                invalid={titleInputInvalid}
                onValueChange={(title: string) => this.setState({
                  title,
                  titleInputInvalid: false,
                })}
              />
            </Label>
          </Tooltip>
          <Label
            labelWrapperStyle={styles['label-container']}
            containerStyle={mergeStyles(
              styles['field'],
              styles['field-mail'],
              // tslint:disable-next-line: no-any
            ) as any}
            labelStyle={styles['label']}
            label={
              <EmailTooltip
                isTooltipOpen={isEmailTooltipOpen}
                handleTooltipePopupClose={() => this.setState({ isEmailTooltipOpen: false })}
                handleTooltipIconClick={() => this.setState({ isEmailTooltipOpen: true })}
              />
            }
          >
            <Tooltip tooltipContent={emailError} position={TooltipPosition.BOTTOM_LEFT}>
              <TextField
                placeholder="Email"
                value={email}
                disabled={isLoad}
                invalid={emailInputInvalid}
                onValueChange={(email: string) => this.setState({
                  email,
                  emailInputInvalid: false,
                  emailError: '',
                })}
              />
            </Tooltip>
          </Label>
          <Label
            containerStyle={styles['field']}
            labelWrapperStyle={styles['label-container']}
            labelStyle={styles['label-notification']}
            label={['Частота', <br/>, 'уведомлений']}
          >
            <DropdownField
              value={this.state.notificationFrequency}
              label={getFormattedLabel(this.state.notificationFrequency)}
              onValueChange={(value: TNotificationFrequencyValue) => this.setState({ notificationFrequency: value })}
              values={NOTIFICATION_FREQUENCY}
              containerStyle={styles['subscription-period']}
              popupStyle={styles['popup-period']}
            />
          </Label>
          <Checkbox
            containerStyle={styles['checkbox']}
            label="Хочу получать новости Cian.ru"
            value={isNews}
            onValueChange={(value: boolean) => this.setState({isNews: value})}
          />
          {
            pushSubscribed &&
              <Checkbox
                containerStyle={styles['checkbox']}
                label="Включить push-уведомления"
                value={isPushCheck}
                onValueChange={(value: boolean) => this.setState({isPushCheck: value})}
              />
          }
          <div className={styles['footer']}>
            <Button
              type="submit"
              className={styles['button']}
              disabled={isLoad}
            >
              Сохранить
            </Button>
            { !(user && user.isAuthenticated) && <License /> }
          </div>
        </form>
      </>
    );
  }

  private handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const { offer, fetchCreateSubscription } = this.props;
    const { title, email, isNews, isPushCheck, notificationFrequency } = this.state;

    const emailError = getEmailError(email);

    if (emailError || !title) {
      this.setState({
        titleInputInvalid: !title,
        emailInputInvalid: Boolean(emailError),
        emailError,
      });

      return;
    }

    if (isPushCheck && window.dataLayer) {
      window.dataLayer.push({ event: 'show_push_cian_site' });
    }

    const notifyFrequency = mapNotificationFrequency(notificationFrequency);

    fetchCreateSubscription({
      title,
      email,
      jsonQuery: getJsonQuery(offer),
      subscribeNews: isNews,
      savedSearchType: ESavedSearchType.BuildingAddress,
      notificationFrequency: notifyFrequency,
    })
      .then(() => {
        trackSubscribeSave(notifyFrequency, isPushCheck);

        if (isNews) {
          trackSubscribeWithNews();
        }
      });
  }
}
